import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { ColumnResizeArgs, DataStateChangeEvent, GridComponent, GridDataResult, RowClassArgs, SelectableSettings, SelectionEvent } from '@progress/kendo-angular-grid';
import { aggregateBy, orderBy } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';

import { DataDictionaryApi } from '../../../api/data-dictionary-api';
import { LayerApi } from '../../../api/layer-api';
import { util } from '../../../classes/util';
import { BooleanConverter, InputConverter, NumberConverter } from '../../../inputConverter';
import { keys } from '../../../keys';
import { ExportService } from '../../../services/export.service';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { IUserSettingsService } from '../../../services/interfaces/IUserSettingsService';
import { LayerService } from '../../../services/layer.service';
import { StorageService } from '../../../services/storage.service';
import { ToSqlStringService } from '../../../services/to-sql-string.service';
import {
    ColumnSortOrder, IColumnSetting, IColumnSettingsStorage, IExportParameters, ILayer, ILayerColumn,
    ILayerSettings,
    IMapcoreProps, IOperatorOption, IQuery, ISmallColumnSetting, ISortSetting
} from '../../../types';
import { BaseComponent } from '../../base.component';
/* tslint:disable:template-cyclomatic-complexity */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-data-table-ng',
    styleUrls: [
        '../../sharedStyles/base.scss',
        '../../sharedStyles/telerik.scss',
        './data-table.component.scss'
    ],
    templateUrl: './data-table.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableComponent extends BaseComponent implements AfterViewInit {
    geoSnapTableId: number = 161;
    aggregates: any[] = [];

    @Input()
    get columnSortOrder() { return this._columnSortOrder; }
    set columnSortOrder(val: ColumnSortOrder) {
        if (val !== this._columnSortOrder) {
            this._columnSortOrder = val;
            (async () => {
                await this.accessingLocalStorage;
                if (this.updateStorage) {
                    const hasKey = await this.localStorageService.hasKey(this.columnSettingsStorageKey);
                    if (hasKey) {
                        await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'sortBy', this._columnSortOrder, 'data-table');
                    } else {
                        await this.createOrUpdateFromLocalStorage('order');
                    }
                }
                this.setColumnSettingsFilteredForDialog();
            })();
        }
    }
    _columnSortOrder: ColumnSortOrder = 'database';
    columnSettingsFilterText: string = '';
    columnSettingsSortField: SettingSortType = 'order';
    columnSettingsSortDescending: boolean = false;
    columnSettingsTimer?: number;
    columnSettingBeingDragged: IColumnSetting;
    columnSettingElementBeingDragged: any;
    gridColumns: IColumnSetting[] = [];
    get visibleGridColumns(): IColumnSetting[] {
        return this.gridColumns ? this.gridColumns.filter(c => c.visible) : [];
    }
    gridDataResult: GridDataResult;
    fullData: any[] = [];
    layerRef: ILayer;
    loading: boolean;
    mouseDownStartX: number;
    mouseDownStartY: number;
    objectIdColumnName: string;
    operatorOptions: IOperatorOption[];
    showFooter: boolean = false;
    footerText: any = {};
    rowCount: string = '';
    get rowCountNumber() {
        return !!this.rowCount ? parseInt(this.rowCount.replace(/,/g, ''), 10) : 0;
    }
    selectedObjectIds: number[] = [];
    showSelected: boolean = false;
    parentTabs; //: TabsComponent; // todo TFS#13854
    parentTabName: string;
    localStorageService: StorageService;
    updateStorage: boolean = true;
    layerChildTables: any[] = null;
    childTableChildParentKeyColumnNames: string[] = null;
    featureChildTables: any[] = null;
    tableIdToTableDictionary: { [tableId: number]: any[] } = {};
    showCloseOptions = false;
    hideCustomSort: boolean = true;

    enableMultiTabs = false;
    multiTabsArr: { name: string; layerId: string; maxFetchRecord?: number }[] = [];
    curMultiTab: string;
    @Output() public readonly closeTab = new EventEmitter<string>();
    @Output() public readonly ondrawcomplete = new EventEmitter<void>();
    @Output() public readonly ondraw = new EventEmitter<void>();
    @Output() public readonly openEditor = new EventEmitter<{ layerId: string; objectId?: number }>();

    @Input() @InputConverter(BooleanConverter) columnSettingsDialogOpened = false;

    @Input()
    get columnSettings() { return this._columnSettings; }
    set columnSettings(val: IColumnSetting[]) {
        if (val !== this._columnSettings) {
            this._columnSettings = val;
            (async () => {
                await this.accessingLocalStorage;
                if (this.updateStorage) {
                    const hasKey = await this.localStorageService.hasKey(this.columnSettingsStorageKey);
                    if (hasKey) {
                        await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'settings', this.mapColumnsForSave(this._columnSettings), 'data-table');
                    } else {
                        await this.createOrUpdateFromLocalStorage('settings');
                    }
                }
                this.setColumnCheckboxes();
                this.setColumnSettingsFilteredForDialog();
            })();
        }
    }
    _columnSettings: IColumnSetting[] = [];

    @Input() dataFetched: { [key: string]: DataItem }[] = [];
    @Input() @InputConverter(NumberConverter) delayForFetchingNewColumnData: number = 800;
    @Input() layerColumns: ILayerColumn[] = [];
    @Input()
    @InputConverter(BooleanConverter)
    get opened() { return this._opened; }
    set opened(val: boolean) {
        this._opened = val;
        if (this._opened) {
            if (this.mapcoreProps && this.mapcoreProps.dataTableArr && this.mapcoreProps.dataTableArr[this.mapcorePropsIndex]) {
                if (!this.mapcoreProps.dataTableArr[this.mapcorePropsIndex].maxFetchRecord) {
                    this.mapcoreProps.dataTableArr[this.mapcorePropsIndex].maxFetchRecord = this.defaultRecordsFetched;
                } else {
                    this.selectedRecordFetch = this.mapcoreProps.dataTableArr[this.mapcorePropsIndex].maxFetchRecord;
                }
            }
            window.addEventListener('itemInserted', this.storageUpdated.bind(this));
        } else {
            window.removeEventListener('itemInserted', this.storageUpdated.bind(this));
        }
    }
    _opened: boolean = false;

    @Input() get LayerService() {
        return this.layerService;
    }
    @Input() @InputConverter(NumberConverter) pageSize: number = 20;

    @Input()
    @InputConverter(BooleanConverter)
    recordEditorEnabled = false;

    @Input() selectableSettings: SelectableSettings = {
        checkboxOnly: true,
        mode: 'multiple'
    };
    @Input() titleProperty: string = 'name';
    @Input() toolbarPosition: 'top' | 'bottom' | 'both' = 'top';
    @Input() @InputConverter(NumberConverter) maxDefaultColumns: number = 10; // When insufficient columnSettings are supplied. We fill up the gridColumns to this number.
    @Input() @InputConverter(NumberConverter) maxRecordsFetched: number = 10000000;
    @Input() @InputConverter(NumberConverter) defaultRecordsFetched: number = 5000;
    @Input() @InputConverter(BooleanConverter) enableAdditionalInformation: boolean = false;

    @Input() @InputConverter(NumberConverter) scrollSize: number = 20;
    @Input() @InputConverter(NumberConverter) scrollSpeed: number = 1;

    selectedRecordFetch: number = this.defaultRecordsFetched = this.maxRecordsFetched;
    maxRecordFetchList = [
        { name: '100', value: 100 },
        { name: '500', value: 500 },
        { name: '1,000', value: 1000 },
        { name: '5,000', value: 5000 },
        { name: '10,000', value: 10000 },
        { name: '50,000', value: 50000 },
        { name: '100,000', value: 100000 }];
    _mapcoreProps: IMapcoreProps[] = [];
    mapcorePropsIndex = 0;
    get mapcoreProps() {
        return this._mapcoreProps[this.mapcorePropsIndex];
    }
    @Input() set mapcoreProps(mapcoreProps: IMapcoreProps) {
        const newEsriLayerId: string = mapcoreProps.esriLayerId;

        this.gridDataResult = null;
        this.gridColumns = [];
        this.showSelected = false;
        this._columnSortOrder = 'database';
        this.changeDetectorRef.detectChanges();

        //find if the layer has already been added
        const i = this._mapcoreProps.findIndex(x => newEsriLayerId === x.esriLayerId);
        if (i > -1) {
            //already exist
            this.mapcorePropsIndex = i;
        } else {
            //create new entry
            this._mapcoreProps.push(mapcoreProps);
            this.mapcorePropsIndex = this._mapcoreProps.length - 1;
        }

        if (this._mapcoreProps[this.mapcorePropsIndex].dataTableArr && this._mapcoreProps.length > 1) {
            this.enableMultiTabs = true;
            this.multiTabsArr = this._mapcoreProps[this.mapcorePropsIndex].dataTableArr;
            this.curMultiTab = newEsriLayerId;
        } else {
            this.enableMultiTabs = false;
            this.multiTabsArr = [];
            this.curMultiTab = '';
        }

        if (this.mapcoreProps.state === undefined) {
            this.mapcoreProps.state = {};
            this.mapcoreProps.state.skip = 0;
        }

        if (!mapcoreProps.isExternal && mapcoreProps.permissions && this.userPermissions && this.userPermissions.length) {
            this.availableCababilities = mapcoreProps.permissions.filter(per => this.userPermissions.includes(per.name)).map(per => per.action);
        }

        this.refreshGridData().then(async () => {
            await this.createOrUpdateFromLocalStorage();
            this.changeDetectorRef.detectChanges();
        });
    }

    availableCababilities: string[] = [];
    get canDelete() { return this.availableCababilities.includes('DR'); }
    get canInsert() { return this.availableCababilities.includes('IR'); }
    get canUpdate() { return this.availableCababilities.includes('UR'); }

    userPermissions: string[] = [];

    @ViewChild('lassoTool') lassoTool: any;
    @Input() mapView: NonNullable<__esri.MapView>;

    @ViewChild('grid') grid: GridComponent;

    /* tslint:disable:max-file-line-count */
    constructor(
        _vcr: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly exportService: ExportService,
        private readonly layerService: LayerService,
        private readonly dataDictionaryApi: DataDictionaryApi,
        public toSqlStringService: ToSqlStringService,
        private readonly layerApi: LayerApi,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        @Inject('UserSettingsService') public userSettingsService: IUserSettingsService,
        @Inject('authenticationService') public authService: IAuthenticationService
    ) {
        super(_vcr, true);
        this.registerEvent('closeTab', this.closeTab);
        this.localStorageService = new StorageService({ userSettingsService, authService });
        this.authService.permissions.subscribe(permissions => this.userPermissions = permissions || []);
    }

    async ngAfterViewInit() {
        this.mapView = await this.setupMapViewRef(this.mapView);
    }

    private async storageUpdated(e: CustomEvent<{ key: string; caller?: string }>) {
        if ((!e.detail.caller || e.detail.caller !== 'data-table') && e.detail.key.startsWith(keys.storage.columnSettingsStorage.slice(0, keys.storage.columnSettingsStorage.indexOf('{') - 1))) {
            if (!this.updateStorage) {
                await util.retryUntilValue(() => this.updateStorage ? true : undefined, 2000, 100);
            }
            if (this.updateStorage) {
                this.updateStorage = false;
                await this.createOrUpdateFromLocalStorage();
                this.refreshColumnSettings();
                await this.refreshGridData();
                this.changeColumnSortOrder();
                this.updateStorage = true;
            }
        }
    }

    private async setHashedUrl() {
        if (this.mapcoreProps && this.mapcoreProps.url) {
            const hashBuffer = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(this.mapcoreProps.url));
            this.mapcoreProps.hashedUrl = Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
        }
    }

    @Input()
    public refreshGrid = () => {
        this.refreshGridData();
    }

    async refreshGridData() {
        this.loading = true;
        this.rowCount = '';
        if (this.mapcoreProps !== undefined) {
            await this.updateLayerInformationIfNeeded();
            await this.fetchDataForAllVisibleColumns();
            this.loadDataIntoGrid();
        }
    }

    selectForCheckBox(checkBoxIdentifier, event) {
        switch (checkBoxIdentifier) {
            case 'visibility':
                this.columnSettings.forEach(col => col.visible = event.target.checked);
                break;
            case 'sum':
                this.columnSettings.filter(col => col.isNumeric && col.calculateSum !== null).forEach(col => col.calculateSum = event.target.checked);
                break;
            case 'average':
                this.columnSettings.filter(col => col.isNumeric && col.calculateAverage !== null).forEach(col => col.calculateAverage = event.target.checked);
                break;
            default:
                break;
        }
        this.columnSettingChange();
    }

    columnSettingsFilteredForDialog: IColumnSetting[] = [];
    setColumnSettingsFilteredForDialog() {
        if (this.columnSettingsFilterText.length) {
            const searchText = this.columnSettingsFilterText.toLowerCase();
            this.columnSettingsFilteredForDialog = this.columnSettings.filter(x => x.name.toLowerCase().indexOf(searchText) >= 0 || x.displayName.toLowerCase().indexOf(searchText) >= 0);
        } else {
            this.columnSettingsFilteredForDialog = [...this.columnSettings];
        }
        this.sortColumns(this.columnSettingsFilteredForDialog);
        this.changeDetectorRef.detectChanges();
    }

    get systemLayerId() {
        let systemLayerId = 0;

        if (this.mapcoreProps) {
            systemLayerId = this.mapcoreProps.id || this.mapcoreProps.idFromSavedLayer || this.mapcoreProps.parentId;
        }

        return systemLayerId;
    }

    get tableId() {
        if (this.mapcoreProps && this.mapcoreProps.tables && this.mapcoreProps.tables[0].id) {
            return this.mapcoreProps.tables[0].id;
        }
        return 0;
    }

    get columnSettingsStorageKey() {
        let storageKey: string;
        if (this.mapcoreProps) {
            const projectIdClause = this.toSqlStringService.getPetlProjectIdClauseIfPresent(this.mapcoreProps.parentId, this.mapcoreProps.query, this.mapcoreProps.isPETL);
            if (projectIdClause) {
                // use a different storage key for PETL layers
                storageKey = keys.storage.columnSettingsStorage.replace('{layer_id}', 'petl-' + projectIdClause.value);
            } else if (!this.mapcoreProps.isExternal) {
                storageKey = keys.storage.columnSettingsStorage.replace('{layer_id}', 'table-' + this.tableId.toString());
            } else {
                storageKey = keys.storage.columnSettingsStorage.replace('{layer_id}', 'ex-' + this.mapcoreProps.hashedUrl);
            }
        }
        return storageKey;
    } //**TODO Handle non-mapcore layers**

    get width() {
        return window.innerWidth;
    }

    addCommas(value: number | string) {
        return (typeof value === 'string' ? parseFloat(value) : value).toLocaleString();
    }

    calculateWidth(columnSetting: IColumnSetting): number {
        const displayName = columnSetting.displayName;
        let wideCount = 0;
        let normalCount = 0;
        for (let i = 0; i < displayName.length; i++) {
            const ch = displayName.charAt(i);
            if (
                (ch >= 'A' && ch <= 'Z' && ch !== 'I') ||
                ch === 'm' ||
                ch === 'w'
            ) {
                wideCount++;
            } else {
                normalCount++;
            }
        }
        const startingWidth = 50;
        const pixelsPerWideChar = 12;
        const pixelsPerNormalChar = 7;
        return (
            startingWidth +
            wideCount * pixelsPerWideChar +
            normalCount * pixelsPerNormalChar
        );
    }

    @Input()
    public close = (esriLayerId?: string) => { // This method needs refactored
        if (esriLayerId) {
            const closeIndex = this._mapcoreProps.findIndex(p => p.esriLayerId === esriLayerId);
            if (closeIndex > -1) {
                this.closeTab.emit(esriLayerId);
                this._mapcoreProps.splice(closeIndex, 1);
                if (closeIndex === this.mapcorePropsIndex && this.mapcorePropsIndex >= this._mapcoreProps.length) {
                    this.mapcorePropsIndex = this._mapcoreProps.length - 1;
                }
                if (this._mapcoreProps.length < 2) {
                    this.enableMultiTabs = false;
                    this.multiTabsArr = [];
                    this.curMultiTab = '';
                }
                if (!this._mapcoreProps.length) {
                    this.opened = false;
                    this.dataFetched = [];
                    this.selectedObjectIds = [];
                    this.layerChildTables = [];
                    this.hideCustomSort = true;

                    this.lassoTool.destroy();
                }
            }
        } else if (this._mapcoreProps.length > 1) {
            if (this._mapcoreProps.length === 2) {
                this.enableMultiTabs = false;
                this.multiTabsArr = [];
                this.curMultiTab = '';
            }
            this.closeTab.emit(this.mapcoreProps.esriLayerId);
            this._mapcoreProps.splice(this.mapcorePropsIndex, 1);
            this.mapcorePropsIndex = 0;
        } else {
            this.closeTab.emit(this.mapcoreProps.esriLayerId);
            this.mapcoreProps.dataTableArr = [];
            this._mapcoreProps = [];

            this.opened = false;
            this.dataFetched = [];
            this.selectedObjectIds = [];
            this.layerChildTables = [];
            this.hideCustomSort = true;

            this.lassoTool.destroy();
        }
        this.showCloseOptions = false;
    }

    @Input()
    closeAll = () => {
        if (this._mapcoreProps) {
            while (this._mapcoreProps.length > 0) {
                this.close();
            }
        }
    }

    allVisible = false;
    allSum = false;
    allAverage = false;
    private setColumnCheckboxes() {
        this.allVisible = this.columnSettings.every(col => col.visible);
        this.allSum = this.columnSettings.filter(col => col.isNumeric && col.calculateSum !== null).every(col => col.calculateSum);
        this.allAverage = this.columnSettings.filter(col => col.isNumeric && col.calculateAverage !== null).every(col => col.calculateAverage);
    }

    async columnSettingChange() {
        this.setColumnCheckboxes();
        if (this.columnSettingsTimer) {
            window.clearTimeout(this.columnSettingsTimer);
        }
        this.columnSettingsTimer = window.setTimeout(async () => {
            this.columnSettingsTimer = null;
            await this.populateGridColumnsFromColumnSettings();
            await this.fetchDataIfNeeded();
            this.loadDataIntoGrid();
            this.changeDetectorRef.detectChanges();
        }, this.delayForFetchingNewColumnData);
    }

    async download() {
        if (!this.mapcoreProps.isExternal) {
            const rootQuery: IQuery = { operator: 'AND', subqueries: [] };
            if (this.mapcoreProps.parentQuery) {
                if (!this.mapcoreProps.parentQuery.subqueries) {
                    if (this.mapcoreProps.parentQuery) {
                        rootQuery.subqueries.push(this.mapcoreProps.parentQuery);
                    }
                } else {
                    this.mapcoreProps.parentQuery.subqueries.forEach(subQ => {
                        rootQuery.subqueries.push(subQ);
                    });
                }
            }
            if (this.mapcoreProps.query) {
                if (!this.mapcoreProps.query.subqueries) {
                    if (this.mapcoreProps.query) {
                        rootQuery.subqueries.push(this.mapcoreProps.query);
                    }
                } else {
                    this.mapcoreProps.query.subqueries.forEach(subQ => {
                        rootQuery.subqueries.push(subQ);
                    });
                }
            }
            if (this.mapcoreProps.state.filter) {
                if (this.mapcoreProps.state.filter.filters.length > 0) {
                    const filterQuery: IQuery = { operator: this.mapcoreProps.state.filter.logic.toUpperCase(), subqueries: this.mapcoreProps.state.filter.filters };
                    filterQuery.subqueries.forEach(filter => {
                        if (filter.operator === 'contains') {
                            filter.operator = 'like';
                        }
                    });
                    rootQuery.subqueries.push(filterQuery);
                }

            }

            let stringifiedSort = '';
            if (this.mapcoreProps.state.sort) {
                for (let i = 0; i < this.mapcoreProps.state.sort.length; i++) {
                    if (this.mapcoreProps.state.sort[i].dir === undefined || this.mapcoreProps.state.sort[i].field === undefined) {
                        this.mapcoreProps.state.sort.splice(i, 1);
                        i--;
                    }
                }

                if (this.mapcoreProps.state.sort.length > 0) {
                    stringifiedSort = JSON.stringify(this.mapcoreProps.state.sort);
                } else {
                    stringifiedSort = '';
                }
            }

            const exportParams: IExportParameters = { // TODO External Layers
                layerId: this.systemLayerId,
                query: rootQuery,
                title: this.mapcoreProps.name,
                sort: stringifiedSort
            };
            if (this.selectedObjectIds.length > 0) {
                const query: IQuery = {
                    field: this.objectIdColumnName,
                    operator: 'IN',
                    value: this.selectedObjectIds.join(',')
                };
                rootQuery.subqueries.push(query);
            }
            const fieldNames = this.gridColumns.map((x) => x.name);
            if (this.layerColumns) {
                // if layerColumns are present, pass those as as parameters, but perserve the order of the columns in fieldNames
                exportParams.layerColumns = [];
                for (const fieldName of fieldNames) {
                    const layerColumnsFound = this.layerColumns.filter(x => x.name.toLowerCase().trim() === fieldName.toLowerCase().trim());
                    if (layerColumnsFound.length) {
                        exportParams.layerColumns.push(layerColumnsFound[0]);
                    }
                }
            } else {
                exportParams.fieldNames = fieldNames;
            }
            await this.exportService.exportToExcelAndOpenUrl(exportParams);
        } else {
            const tempData = { ...this.grid.data };
            //assign all data to grid when export to excel
            this.grid.data = {
                data: this.fullData,
                total: this.fullData.length
            };
            this.grid.saveAsExcel();
            this.grid.data = { ...tempData };
        }
    }

    private readonly columnSortByDBOrder = (a: ILayerColumn, b: ILayerColumn): number => {
        if (a.databaseColumnOrder > b.databaseColumnOrder) {
            return 1;
        } else if (a.databaseColumnOrder > b.databaseColumnOrder) {
            return -1;
        }
        return 0;
    }

    async populateLayerInformation() {
        if (this.systemLayerId) {
            const layers = await this.layerApi.getLayerInfo(this.systemLayerId);
            if (layers.length > 0) {
                if (this.mapcoreProps === undefined) {
                    return [];
                }
                this.layerRef = layers[0];
                this.layerColumns.length = 0;
                if (!this.mapcoreProps.columns) {
                    this.layerColumns.push(...layers[0].columns);
                } else {
                    //Use this.mapcoreProps.columns with layers[0]'s databaseDataType mapped to each respective column
                    for (const col of this.mapcoreProps.columns) {
                        //find the related column in layers[0]
                        const layersCol = layers[0].columns.find(x => x.databaseColumnOrder === col.databaseColumnOrder);
                        if (!layersCol) {
                            continue;
                        }
                        //if found, append its listed dataType to mapcoreprops columns list
                        if (layersCol.databaseDataType) {
                            col.databaseDataType = layersCol.databaseDataType;
                        }
                    }
                    this.layerColumns.push(...this.mapcoreProps.columns);
                }
                this.layerColumns.sort(this.columnSortByDBOrder);
                this.objectIdColumnName = this.layerService.getObjectIdColumnName(
                    this.layerRef
                );
                for (const layerColumn of this.layerColumns) {
                    layerColumn.name = layerColumn.name.toLowerCase();
                    if (
                        layerColumn.alias &&
                        layerColumn.alias.toUpperCase() ===
                        layerColumn.name.toUpperCase()
                    ) {
                        layerColumn.alias = layerColumn.name.toUpperCase();
                    }
                }
                return this.layerColumns;
            }
        } else if (this.mapcoreProps.isExternal) {
            this.layerColumns = [...this.mapcoreProps.columns];
            this.layerColumns.sort(this.columnSortByDBOrder);
            const objectIdColumn = this.layerColumns.find(col => col.name.toLowerCase().replace('_', '').trim() === 'objectid');
            this.objectIdColumnName = objectIdColumn ? objectIdColumn.name : null;
            return this.layerColumns;
        }
        return [];
    }

    async fetchDataForAllVisibleColumns() {
        this.dataFetched = [];
        this.selectedObjectIds = [];
        if (!this.gridColumns || !this.gridColumns.length) {
            await this.populateGridColumnsFromColumnSettings();
        }
        let fieldNames = this.gridColumns.map((x) => x.name.toLowerCase().trim());
        if (fieldNames.indexOf(this.objectIdColumnName.toLowerCase().trim()) < 0) {
            fieldNames.push(this.objectIdColumnName.toLowerCase().trim());
        }
        if (this.childTableChildParentKeyColumnNames !== null) {
            fieldNames = [...new Set(fieldNames.concat(this.childTableChildParentKeyColumnNames))];
        }
        if (!this.mapcoreProps.isExternal) {
            await this.fetchDataForSpecifiedColumns(fieldNames);
        } else {
            await this.fetchExternalDataForSpecifiedColumns(fieldNames);
        }
    }

    async fetchExternalDataForSpecifiedColumns(fieldNames: string[]) {
        const result = await this.fetchExternalData(fieldNames);
        this.dataFetched = result.map(row => {
            const item: { [key: string]: DataItem } = {};
            Object.entries(row).forEach(([col, value]) => {
                let colName = col;
                let builtValue: DataItem = value;
                const matchingColumn = this.layerColumns.find(c => c.name.toLowerCase().trim() === col.toLowerCase().trim());
                if (matchingColumn && matchingColumn.domainValues) {
                    colName = matchingColumn.name;
                    let foundDomainValue;
                    if (typeof value === 'number') {
                        foundDomainValue = matchingColumn.domainValues.find(dv => parseFloat(dv.codeValue.toString()) === value);
                    } else {
                        foundDomainValue = matchingColumn.domainValues.find(dv => dv.codeValue === value);
                    }
                    if (foundDomainValue) {
                        builtValue = { value, domainValue: `${foundDomainValue.codeValue} - ${foundDomainValue.shortDescription}` };
                    }
                }
                item[colName] = builtValue;
            });
            return item;
        });
    }

    async fetchDataForSpecifiedColumns(fieldNames: string[]) {
        const rootQuery: IQuery = { operator: 'AND', subqueries: [] };
        if (this.mapcoreProps.parentQuery) {
            rootQuery.subqueries.push(this.mapcoreProps.parentQuery);
        }
        if (this.mapcoreProps.query) {
            rootQuery.subqueries.push(this.mapcoreProps.query);
        }
        this.defaultRecordsFetched = this.maxRecordsFetched;
        if (fieldNames.length > 2) {
            const minRecords = 100000;
            const multFactor = 5;
            const adjustment = 4;
            const roundFactor = 1000;
            const calculatedAmount = Math.round((this.defaultRecordsFetched / ((multFactor * Math.sqrt(fieldNames.length)) - adjustment)) / roundFactor) * roundFactor;
            this.defaultRecordsFetched = Math.max(calculatedAmount, minRecords);
        }
        if (this.mapcoreProps.dataTableArr && this.mapcoreProps.dataTableArr[this.mapcorePropsIndex]) {
            if (!this.mapcoreProps.dataTableArr[this.mapcorePropsIndex].maxFetchRecord) {
                this.selectedRecordFetch = this.mapcoreProps.dataTableArr[this.mapcorePropsIndex].maxFetchRecord = this.defaultRecordsFetched;
            } else {
                this.selectedRecordFetch = this.mapcoreProps.dataTableArr[this.mapcorePropsIndex].maxFetchRecord;
            }
        } else {
            this.selectedRecordFetch = this.defaultRecordsFetched;
        }
        const result: (string | number)[][] = await this.layerApi.getData(
            this.systemLayerId,
            fieldNames,
            rootQuery,
            this.defaultRecordsFetched //this.selectedRecordFetch - for dropdown selection
        );
        if (this.mapcoreProps && this.mapcoreProps.columns && !this.mapcoreProps.columns.some(c => !!c.domainValues) && this.mapcoreProps.metaTableId) {
            const dictionary = await this.dataDictionaryApi.getColumnsWithDomainValuesByMetaTableId(this.mapcoreProps.metaTableId);
            if (dictionary) {
                for (const column of dictionary) {
                    const matchingColumn = this.mapcoreProps.columns.find(c => c.name.toLowerCase().trim() === column.columnName.toLowerCase().trim());
                    if (matchingColumn) {
                        matchingColumn.domainValues = column.domainValues;
                    }
                }
            }
        }
        let columns: ILayerColumn[] = [];
        if (this.mapcoreProps && this.mapcoreProps.columns) {
            columns = fieldNames.map(field => this.mapcoreProps.columns.find(c => c.name.toLowerCase().trim() === field.toLowerCase().trim()) || ({ name: field.toLowerCase().trim() } as ILayerColumn));
        } else {
            columns = fieldNames.map(field => ({ name: field.toLowerCase().trim() } as ILayerColumn));
        }
        this.dataFetched = result.map(row => {
            const item: { [key: string]: DataItem } = {};
            row.forEach((value, i) => {
                let builtValue: DataItem = value;
                const col = columns[i];
                if (col.domainValues) {
                    let foundDomainValue;
                    if (typeof value === 'number') {
                        foundDomainValue = col.domainValues.find(dv => parseFloat(dv.codeValue.toString()) === value);
                    } else {
                        foundDomainValue = col.domainValues.find(dv => dv.codeValue === value);
                    }
                    if (foundDomainValue) {
                        builtValue = { value, domainValue: `${foundDomainValue.codeValue} - ${foundDomainValue.shortDescription}` };
                    }
                }
                item[col.name] = builtValue;
            });
            return item;
        });
    }

    async fetchDataIfNeeded() {
        if (this.dataFetched.length > 0) {
            const fieldNames = this.gridColumns.map((x) => x.name.toLowerCase().trim());
            const dataKeys = Object.keys(this.dataFetched[0]).map(x => x.toLowerCase().trim());
            if (fieldNames.some(fieldName => !dataKeys.includes(fieldName))) {
                await this.fetchDataForAllVisibleColumns();
            }
        }
    }

    async fetchExternalData(outFields?: string[]) {
        const formatResponse = (response: __esri.FeatureSet) => {
            return response.features.map(feature => {
                return Object.keys(feature.attributes).reduce<{ [key: string]: any }>((acc, key) => {
                    acc[key.toLowerCase()] = feature.attributes[key];
                    return acc;
                }, {});
            });
        };

        const featureLayer = this.layerService.mapLayers.feature.find(element => element.mapcoreProps.guid === this.mapcoreProps.guid);
        const results: { [key: string]: any }[] = [];
        if (featureLayer) {
            const query = featureLayer.createQuery();
            query.outFields = outFields;
            query.num = this.maxRecordsFetched;
            let keepRequesting = true;
            while (keepRequesting) {
                query.start = results.length;
                const response = await featureLayer.queryFeatures(query);
                if (!results.length) {
                    query.num = response.features.length;
                }
                results.push(...formatResponse(response));
                keepRequesting = response.exceededTransferLimit;
            }
        }
        return results;
    }

    async fetchLayerChildTables() {
        if (!this.layerChildTables) {
            this.layerChildTables = await this.layerService.getLayerChildTables(this.systemLayerId);
            const filteredChildTables = this.layerChildTables.filter(childTable => childTable.tableId !== this.geoSnapTableId); // Fix for geosnap having invalid childParentKeyColumnName
            this.childTableChildParentKeyColumnNames = [...new Set(filteredChildTables.map(table => table.childParentKeyColumnName.toLowerCase().trim()))];
            this.childTableChildParentKeyColumnNames.push('objectid'); // add this in for 161 geosnap
        }
    }

    async updateLayerInformationIfNeeded() {
        if (this.mapcoreProps.isExternal) {
            await this.setHashedUrl();
        }

        this.updateStorage = false;
        this.columnSettings = (await this.populateLayerInformation()).map(lc => this.getRefreshedColumnSetting(lc));
        this.updateStorage = true;
        this.refreshColumnSettings();
        await this.createOrUpdateFromLocalStorage();
        await this.populateGridColumnsFromColumnSettings();
        this.sortColumns(this.gridColumns);

        if (this.systemLayerId && (!this.layerRef || this.layerRef.id !== this.systemLayerId)) {
            if (this.enableAdditionalInformation) {
                await this.fetchLayerChildTables();
            }
        }
    }

    cdkDrop(event: CdkDragDrop<null>) {
        if (this.columnSortOrder !== 'custom') {
            this.sortColumns(this._columnSettings);
        }
        moveItemInArray(this.columnSettings, event.previousIndex, event.currentIndex);
        this.columnSortOrder = 'custom';
        this.hideCustomSort = false;
        (async () => {
            await this.accessingLocalStorage;
            await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'enableCustom', !this.hideCustomSort, 'data-table');
            await this.columnSettingChange();
            this.setColumnSettingsFilteredForDialog();
        })();
    }

    dataStateChange(state: DataStateChangeEvent) {
        this.mapcoreProps.state = state;
        this.loadDataIntoGrid();
    }

    columnResized(event: ColumnResizeArgs[]) {
        let changes = false;
        for (const colResize of event) {
            const match = this.columnSettings.find(col => col.name.toLowerCase() === (colResize.column as any).field.toLowerCase());
            if (match) {
                match.width = colResize.newWidth;
                changes = true;
            }
        }
        if (changes) {
            (async () => {
                await this.accessingLocalStorage;
                await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'settings', this.mapColumnsForSave(this.columnSettings), 'data-table');
            })();
        }
    }

    moveItemToTop(columnSetting: IColumnSetting) {
        if (this.columnSortOrder !== 'custom') {
            this.sortColumns(this._columnSettings);
        }
        this.columnSettings.splice(0, 0, ...this.columnSettings.splice(this.columnSettings.indexOf(columnSetting), 1));
        this.columnSortOrder = 'custom';
        this.hideCustomSort = false;
        (async () => {
            await this.accessingLocalStorage;
            await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'enableCustom', !this.hideCustomSort, 'data-table');
            await this.columnSettingChange();
            this.setColumnSettingsFilteredForDialog();
        })();
    }

    getRefreshedColumnSetting(
        layerColumn: ILayerColumn,
        columnSetting: IColumnSetting = null
    ) {
        if (!columnSetting) {
            columnSetting = { ...layerColumn };
        }
        columnSetting.visible =
            columnSetting.visible || columnSetting.visible === undefined;
        columnSetting.isNumeric =
            ['FLOAT', 'NUMBER'].indexOf(layerColumn.databaseDataType) >= 0;
        columnSetting.displayName = layerColumn.name.toUpperCase();
        if (layerColumn.alias) {
            columnSetting.name = columnSetting.name.toUpperCase();
            columnSetting.displayName = layerColumn.alias;
        }
        if (!columnSetting.width) {
            columnSetting.width = this.calculateWidth(columnSetting);
        }
        if (layerColumn.databaseColumnOrder) {
            columnSetting.databaseColumnOrder = layerColumn.databaseColumnOrder;
        }
        return columnSetting;
    }

    loadDataIntoGrid() {
        if (this.mapcoreProps) {
            let dataForGrid = this.dataFetched;

            if (this.mapcoreProps.state.filter) {
                const filters = this.mapcoreProps.state.filter.filters;
                for (const filter of filters) {
                    const field = filter.field;
                    const filterValue = filter.value.toLowerCase();
                    dataForGrid = dataForGrid.filter((x) => {
                        const col = x[field];
                        let retVal = -1;
                        if (col) {
                            if (typeof col === 'object' && col.domainValue) {
                                retVal = col.domainValue.toString().toLowerCase().indexOf(filterValue);
                                if (retVal === -1) {
                                    retVal = col.value.toString().toLowerCase().indexOf(filterValue);
                                }
                            } else {
                                retVal = col.toString().toLowerCase().indexOf(filterValue);
                            }
                        }
                        return retVal >= 0;
                    });
                }
            }
            if (this.showSelected) {
                dataForGrid = dataForGrid.filter(x => this.selectedObjectIds.indexOf(x[this.objectIdColumnName] as number) >= 0);
            }
            if (this.mapcoreProps.state.sort) {
                // Telerik's orderBy doesn't work on object values, so hold those to do the sort and they reapply them
                dataForGrid.forEach((val, i) => val.oldIndex = i);
                let holdData: Map<number, Map<string, IDataItem>>;
                [dataForGrid, holdData] = dataForGrid.reduce<[{ [key: string]: any }[], Map<number, Map<string, IDataItem>>]>((accu, row) => {
                    // Create map of object values to hold
                    const holdMap = new Map<string, IDataItem>();
                    const newRow: { [key: string]: any } = {};
                    for (const field of Object.keys(row)) {
                        const col = row[field];
                        // If the value is a Data Item object, store it with the field as a reference
                        if (this.isDataItem(col)) {
                            // For future reference, if the field should be sorted on the domain value instead of the DB value, this is where that should be checked
                            holdMap.set(field, col);
                            newRow[field] = col.value;
                        } else {
                            newRow[field] = col;
                        }
                    }
                    // Store the values map to a map based on the original index before sorting
                    accu[1].set(row.oldIndex as number, holdMap);
                    // Update the original data to remove the Data Item object
                    accu[0].push(newRow);
                    return accu;
                }, [[], new Map<number, Map<string, IDataItem>>()]);
                // Put the held data back after sorting
                dataForGrid = orderBy(dataForGrid, this.mapcoreProps.state.sort).map(row => {
                    if (holdData.has(row.oldIndex as number)) {
                        const heldRowData = holdData.get(row.oldIndex as number);
                        for (const field of Object.keys(row)) {
                            if (heldRowData.has(field)) {
                                row[field] = heldRowData.get(field);
                            }
                        }
                    }
                    return row;
                });
            }
            this.detectUrls(dataForGrid);
            this.fullData = dataForGrid;
            this.populateAggregates();

            const skipAsNumber: number = Number(this.mapcoreProps.state.skip);
            this.gridDataResult = {
                data: dataForGrid.slice(skipAsNumber, skipAsNumber + this.pageSize),
                total: dataForGrid.length
            };
            this.rowCount = this.addCommas(dataForGrid.length);
            this.loading = false;

            this.changeDetectorRef.detectChanges();
        }
    }

    public hasChildTables = (): boolean => {
        if (this.layerChildTables) {
            return true; // TODO: We need to know if this individual feature actually has data in any childTables
        } else {
            return false;
        }
    }

    expandDetailsBy = (dataItem: any): any => {
        return dataItem.objectid;
    }

    /**
     * Modifies the data passed in to add isUrl boolean if a url is detected for a column
     * @param dataForGrid Array of data rows
     */
    detectUrls(dataForGrid: { [key: string]: any }[]): void {
        if (dataForGrid && dataForGrid.length) {
            // Find the url column first
            const urlColumns: string[] = [];
            const nonurlColumns: string[] = [];
            const columnLength = Object.keys(dataForGrid[0]).length;
            for (const row of dataForGrid) {
                // Iterate over every row of data
                for (const col in row) {
                    // Iterate over every column in the row
                    if (
                        !nonurlColumns.includes(col) &&
                        !urlColumns.includes(col)
                    ) {
                        // Only check columns that we don't know about yet
                        const value = row[col];
                        if (value !== null) {
                            // Only check if not null, as the url column could be nullable
                            const columnSetting = this.columnSettings.find(
                                (cs) => cs.name === col
                            );
                            if (columnSetting && (columnSetting.isUrl || this.isUrl(value))) {
                                urlColumns.push(col);
                            } else {
                                nonurlColumns.push(col);
                            }
                        }
                    }
                }
                if (columnLength === urlColumns.length + nonurlColumns.length) {
                    break;
                } // If all columns have been checked, stop checking completly
            }
            if (urlColumns.length) {
                for (const columnSetting of this.columnSettings) {
                    if (urlColumns.includes(columnSetting.name)) {
                        columnSetting.isUrl = true;
                    } else {
                        columnSetting.isUrl = false;
                    }
                }
            }
        }
    }

    isUrl(value) {
        return (
            value &&
            typeof value === 'string' &&
            /^http[s]?:\/\/(?:[a-zA-Z]|[0-9]|[$-_@.&+]|[!*(), ]|(?:%[0-9a-fA-F][0-9a-fA-F]))+$/.test(
                value.trim()
            )
        );
    }

    mapSelectedFeatures() {
        if (this.selectedObjectIds.length > 0) {
            const query: IQuery = {
                field: this.objectIdColumnName,
                operator: 'IN',
                value: this.selectedObjectIds.join(',')
            };
            if (!this.mapcoreProps.isExternal) {
                const layerSettings = {
                    id: this.systemLayerId,
                    query,
                    zoomToLayer: true
                };
                this.layerService.addMapcoreLayersToMap([layerSettings]);
            } else {
                const featureLayer = this.layerService.mapLayers.feature.find(element => element.mapcoreProps.guid === this.mapcoreProps.guid);
                this.layerService.addExternalLayersToMap({
                    url: this.mapcoreProps.url,
                    name: featureLayer.title,
                    visible: true,
                    zoomToLayer: true
                } as ILayerSettings,
                    { ...this.mapcoreProps, query } as IMapcoreProps);
            }
            if (this.lassoTool) {
                this.lassoTool.clearFeatureResults();
            }
        }
    }

    multiPropertySort(sortSettings: ISortSetting[]) {
        return (a, b) => {
            for (const sortSetting of sortSettings) {
                const name = sortSetting.propertyName;
                const descending = sortSetting.descending;
                if (a[name] < b[name]) {
                    return descending ? 1 : -1;
                }
                if (a[name] > b[name]) {
                    return descending ? -1 : 1;
                }
            }
            return 0;
        };
    }

    async populateGridColumnsFromColumnSettings() {
        const visibleColumnSettings = this.columnSettings.filter(x => x.visible);

        //update local storage columnsettings if needed
        if (this.mapcoreProps !== undefined) {
            await this.accessingLocalStorage;
            const settingsStorage = await this.localStorageService.get<IColumnSettingsStorage>(this.columnSettingsStorageKey);
            if (this.updateStorage) {
                if (!settingsStorage || settingsStorage.settings.length === 0) {
                    //first time
                    this.hideCustomSort = true;
                    await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'settings', this.mapColumnsForSave(this.columnSettings), 'data-table');
                } else if (JSON.stringify(settingsStorage.settings) !== JSON.stringify(this._columnSettings)) {
                    //consecutive times
                    if (settingsStorage.sortBy) {
                        this.columnSortOrder = settingsStorage.sortBy;
                        if (settingsStorage.sortBy === 'custom') {
                            this.hideCustomSort = false;
                        }
                    }
                    await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'settings', this.mapColumnsForSave(this.columnSettings), 'data-table');
                } else if (settingsStorage.sortBy) {
                    this.columnSortOrder = settingsStorage.sortBy;
                }
            }
        }

        this.sortColumns(visibleColumnSettings);
        this.gridColumns = [];
        for (const columnSetting of visibleColumnSettings) {
            const gridColumn: IColumnSetting = { ...columnSetting };
            gridColumn.name = gridColumn.name.toLowerCase().trim();
            this.footerText[gridColumn.name] = '';
            this.gridColumns.push(gridColumn);
        }

        const geometryColumn = this.columnSettings.find(c => c.name.toLowerCase().trim() === 'has_geometry');
        if (geometryColumn && !this.gridColumns.find(c => c.name === geometryColumn.name)) {
            const geometryColumnSetting = { ...geometryColumn };
            geometryColumnSetting.visible = false;
            this.gridColumns.push(geometryColumnSetting);
        }
    }

    populateValidColumnSettings() {
        for (let i = 0; i < this.columnSettings.length; i++) {
            let columnSetting = this.columnSettings[i];
            const foundLayerColumns = this.layerColumns.filter(
                (x) => x.name.toLowerCase() === columnSetting.name.toLowerCase()
            );
            if (foundLayerColumns.length > 0) {
                const layerColumn = foundLayerColumns[0];
                columnSetting = this.getRefreshedColumnSetting(
                    layerColumn,
                    columnSetting
                );
            } else {
                this.columnSettings.splice(i, 1);
                i--;
            }
        }
    }

    populateColumnSettingsToMax() {
        let visibleColumnSettings = this.columnSettings.filter((x) => x.visible)
            .length;
        for (const layerColumn of this.layerColumns) {
            if (
                this.columnSettings.filter(
                    (x) =>
                        x.name.toLowerCase() === layerColumn.name.toLowerCase()
                ).length === 0
            ) {
                const columnSetting = this.getRefreshedColumnSetting(
                    layerColumn
                );
                if (visibleColumnSettings <= this.maxDefaultColumns) {
                    visibleColumnSettings++;
                } else {
                    columnSetting.visible = false;
                }
                this.columnSettings.push(columnSetting);
            }
        }
    }

    populateAggregates() {
        this.setAggregateArray();
        this.populateAggregateValues();
    }

    populateAggregateValues() {
        const fractionDigits = 4;
        if (this.aggregates && this.aggregates.length) {
            const data = this.fullData;
            const aggregateValues = aggregateBy(data, this.aggregates);
            for (const field of Object.keys(aggregateValues)) {
                const gridColumn = this.gridColumns.find(c => c.name.toLowerCase().trim() === field.toLowerCase().trim());
                if (gridColumn) {
                    gridColumn.footerText = '';
                    const columnAggregates: IColumnAggregates = aggregateValues[field];
                    if (columnAggregates.average !== undefined && columnAggregates.average !== null) {
                        gridColumn.average = columnAggregates.average;
                        const averageText = gridColumn.average.toFixed(fractionDigits);
                        gridColumn.footerText += 'Avg: ' + this.addCommas(averageText);
                    }
                    if (columnAggregates.sum !== undefined && columnAggregates.sum !== null) {
                        gridColumn.sum = columnAggregates.sum;
                        if (Math.floor(gridColumn.sum) === gridColumn.sum) {
                            gridColumn.footerText += ' Sum: ' + this.addCommas(gridColumn.sum);
                        } else {
                            gridColumn.footerText += ' Sum: ' + this.addCommas(gridColumn.sum.toFixed(fractionDigits));
                        }
                    }
                    gridColumn.footerText = gridColumn.footerText.trim();
                    this.footerText[field.toLowerCase().trim()] = gridColumn.footerText;
                }
            }
        }
    }

    refreshColumnSettings() {
        this.populateValidColumnSettings();
        this.populateColumnSettingsToMax();
    }

    setAggregateArray() {
        const aggregates = [];
        for (const columnSetting of this.columnSettings) {
            if (columnSetting.calculateSum) {
                aggregates.push({
                    field: columnSetting.name.toLowerCase(),
                    aggregate: 'sum'
                });
            }
            if (columnSetting.calculateAverage) {
                aggregates.push({
                    field: columnSetting.name.toLowerCase(),
                    aggregate: 'average'
                });
            }
        }
        this.showFooter = aggregates.length > 0;
        this.aggregates = aggregates;
    }

    sortColumns = (columns: IColumnSetting[]) => {
        const alphaSort = (a: IColumnSetting, b: IColumnSetting) => {
            const aComp = a.displayName.toUpperCase();
            const bComp = b.displayName.toUpperCase();
            if (aComp.localeCompare(bComp) > 1) {
                return 1;
            } else if (aComp.localeCompare(bComp) < 1) {
                return -1;
            }
            return 0;
        };
        const dbSort = (a: IColumnSetting, b: IColumnSetting) => {
            if (a.databaseColumnOrder > b.databaseColumnOrder) {
                return 1;
            } else if (a.databaseColumnOrder < b.databaseColumnOrder) {
                return -1;
            }
            return 0;
        };
        const findColumnIndex = (cs: IColumnSetting) =>

            this.columnSettings.findIndex(
                (c: IColumnSetting) => c.name.toUpperCase() === cs.name.toUpperCase());
        const customSort = (a: IColumnSetting, b: IColumnSetting) => {
            const aIndex = findColumnIndex(a);
            const bIndex = findColumnIndex(b);
            if (aIndex > bIndex) {
                return 1;
            } else if (aIndex < bIndex) {
                return -1;
            }
            return 0;
        };

        if (columns) {
            switch (this.columnSortOrder) {
                case 'alphabetical':
                    columns.sort(alphaSort);
                    break;
                case 'custom':
                    columns.sort(customSort);
                    break;
                case 'database':
                    columns.sort(dbSort);
                    break;
                default:
                    break;
            }
        }
    }

    changeColumnSortOrder() {
        this.sortColumns(this.gridColumns);
        this.loadDataIntoGrid();
    }

    async changeRecordFetch() {
        this.loading = true;
        this.mapcoreProps.dataTableArr[this.mapcorePropsIndex].maxFetchRecord = this.selectedRecordFetch;
        await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'selectedRecordFetch', this.selectedRecordFetch, 'data-table');
        await this.columnSettingChange();
        this.loading = false;
    }

    changeCurMultiTab() {
        //find the the mapcorePropsIndex that has the selected layerId
        const i = this._mapcoreProps.findIndex(x => this.curMultiTab === x.esriLayerId);
        if (i > -1) {
            this.mapcoreProps = this._mapcoreProps[i];
            this.selectedRecordFetch = this.mapcoreProps.dataTableArr[i].maxFetchRecord;
        }
        if (this.lassoTool) {
            this.lassoTool.deactivate();
            this.lassoTool.clearFeatureResults();
        }
    }

    sortColumnSettings() {
        const sortSettings: ISortSetting[] = [];
        sortSettings.push({
            propertyName: this.columnSettingsSortField,
            descending: this.columnSettingsSortDescending
        });
        if (this.columnSettingsSortField === 'visible') {
            sortSettings[0].descending = !sortSettings[0].descending;
        }
        return this.multiPropertySort(sortSettings);
    }

    toggleColumnSettings() {
        this.columnSettingsDialogOpened = !this.columnSettingsDialogOpened;
    }

    toggleColumnVisibility(columnSetting: IColumnSetting) {
        columnSetting.visible = !columnSetting.visible;
        this.columnSettingChange();
    }

    toggleShowSelected() {
        this.showSelected = !this.showSelected;
        this.loadDataIntoGrid();
    }

    toggleSortColumnSettings(sortField: SettingSortType) {
        if (this.columnSettingsSortField === sortField) {
            this.columnSettingsSortDescending = !this.columnSettingsSortDescending;
        } else {
            this.columnSettingsSortDescending = false;
        }
        this.columnSettingsSortField = sortField;
    }

    private accessingLocalStorage: Promise<void> = Promise.resolve();
    async createOrUpdateFromLocalStorage(skip: 'settings' | 'order' = null) {
        this.accessingLocalStorage = (async () => {
            //See if this layer already has settings stored in localStorageService
            const hasKey = await this.localStorageService.hasKey(this.columnSettingsStorageKey);
            if (hasKey) {
                const settingsStorage = await this.localStorageService.get<IColumnSettingsStorage>(this.columnSettingsStorageKey);
                if (skip === 'settings') {
                    this._columnSettings = this.mapColumnsFromSaved(this.columnSettings, settingsStorage ? settingsStorage.settings : []);
                } else {
                    this.columnSettings = this.mapColumnsFromSaved(this.columnSettings, settingsStorage ? settingsStorage.settings : []);
                }
                if (skip === 'order') {
                    this._columnSortOrder = settingsStorage && settingsStorage.sortBy ? settingsStorage.sortBy : 'database';
                } else {
                    this.columnSortOrder = settingsStorage && settingsStorage.sortBy ? settingsStorage.sortBy : 'database';
                }
                if (settingsStorage.selectedRecordFetch) {
                    this.selectedRecordFetch = settingsStorage.selectedRecordFetch;
                }
                if (settingsStorage && settingsStorage.enableCustom) {
                    this.hideCustomSort = false;
                }
            } else {
                //If local storage does not exist, create it
                //and add the current visibility settings to the column visibility object in local storage for use in the Identify window
                const columnStorageSettings: IColumnSettingsStorage = { showAll: false, sortBy: this.columnSortOrder, settings: this.mapColumnsForSave(this.columnSettings), enableCustom: !this.hideCustomSort };
                if (this.systemLayerId > 0) {
                    await this.localStorageService.set(this.columnSettingsStorageKey, columnStorageSettings, 'data-table');
                }
            }
        })();
        return await this.accessingLocalStorage;
    }

    toggleCloseOptions() {
        this.showCloseOptions = !this.showCloseOptions;
    }

    selectAllToggle() {
        if (this.lassoTool) {
            this.lassoTool.clearFeatureResults();
        }
        if (this.selectedObjectIds.length) {
            this.selectedObjectIds = [];
        } else {
            if (this.dataFetched.length) {
                const objectIdKey = Object.keys(this.dataFetched[0]).find(k => k.toLowerCase() === 'objectid');
                if (objectIdKey) {
                    this.selectedObjectIds = this.dataFetched.map(d => typeof d[objectIdKey] === 'object' ? (<IDataItem>d[objectIdKey]).value : (<number | string>d[objectIdKey])).filter(oid => !!oid) as number[];
                }
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    rowClass(context: RowClassArgs) {
        const retVal = { noGeometry: false };
        if (Object.keys(context.dataItem).includes('has_geometry') && context.dataItem.has_geometry === 'N') {
            retVal.noGeometry = true;
        }
        return retVal;
    }

    selectFromMap() {
        this.lassoTool.deactivate();
        this.lassoTool.containerElement = this.hostElement;
        const subs: Subscription[] = [];
        subs.push(this.lassoTool.oncomplete.subscribe(() => {
            const featureresults: { [key: string]: any; attributes: { [key: string]: any } }[] = (<any[]>this.lassoTool.layerFeatureResults.map(r => r.featureResults)).flat();
            if (featureresults.length) {
                const frObjectIdKey = Object.keys(featureresults[0].attributes).find(key => key.toLowerCase() === 'objectid');
                if (frObjectIdKey) {
                    const selectedIds = featureresults.map(result => result.attributes[frObjectIdKey]);
                    this.selectedObjectIds = [...new Set(selectedIds)];
                }
            }
            if (this.dataFetched.length) {
                const objectIdKey = Object.keys(this.dataFetched[0]).find(k => k.toLowerCase() === 'objectid');
                if (objectIdKey) {
                    this.selectedObjectIds = this.selectedObjectIds.filter(id => !!this.dataFetched.find(d => d[objectIdKey] === id));
                }
            }
            this.lassoTool.deactivate();
            subs.forEach(sub => sub.unsubscribe());
            this.loading = false;
            this.changeDetectorRef.detectChanges();
        }));
        subs.push(this.lassoTool.drawEnd.subscribe(() => {
            this.selectedObjectIds = [];
            this.loading = true;
            this.ondrawcomplete.emit();
            this.changeDetectorRef.detectChanges();
        }));
        this.lassoTool.spatialQueryType = 'feature';
        this.lassoTool.runSpatialQueryOnSketchComplete = true;
        this.lassoTool.reactivateSketchOnSketchComplete = false;
        this.lassoTool.displayFeatureGraphicsAfterSpatialQuery = true;
        this.lassoTool.sketchDefaultCreateOptions = { mode: 'freehand' };
        this.lassoTool.mapView = this.mapView;
        this.lassoTool.esriLayers = [this.layerService.getExistingEsriLayerById(this.mapcoreProps.esriLayerId)];
        this.lassoTool.clearFeatureResults();
        this.lassoTool.activate();
        this.ondraw.emit();
    }

    selectionChange(_event: SelectionEvent) {
        if (this.lassoTool) {
            this.lassoTool.clearFeatureResults();
        }
    }

    triggerOpenEditor(asEdit = false) {
        const objectId = asEdit ? this.selectedObjectIds[0] : null;
        this.openEditor.emit({ layerId: this.mapcoreProps.esriLayerId, objectId });
    }

    private mapColumnsForSave(columnSettings: IColumnSetting[]) {
        return columnSettings.map(column => ({
            name: column.name,
            visible: column.visible,
            width: column.width,
            displayName: column.displayName,
            databaseDataType: column.databaseDataType,
            databaseColumnOrder: column.databaseColumnOrder,
            columnIdentifierId: column.columnIdentifierId,
            isNumeric: column.isNumeric,
            calculateAverage: column.calculateAverage,
            calculateSum: column.calculateSum
        } as ISmallColumnSetting));
    }

    private mapColumnsFromSaved(columnSettings: IColumnSetting[], fromSaved: ISmallColumnSetting[]) {
        columnSettings.forEach(column => {
            const match = fromSaved.find(x => x.name === column.name);
            if (match) {
                column.visible = match.visible;
                column.width = match.width;
                column.calculateAverage = match.calculateAverage;
                column.calculateSum = match.calculateSum;
            }
        });
        return columnSettings.sort((a, b) => {
            const aIndex = fromSaved.findIndex(x => x.name === a.name);
            const bIndex = fromSaved.findIndex(x => x.name === b.name);
            return aIndex - bIndex;
        });
    }

    isDataItem(value: DataItem): value is IDataItem {
        return value !== null && typeof value === 'object';
    }
}

export interface IColumnAggregates {
    average?: number;
    sum?: number;
}

type DataItem = IDataItem | string | number;
interface IDataItem { value: string | number; domainValue: string; }

type SettingSortType = 'displayName' | 'order' | 'visible' | 'calculateSum' | 'calculateAverage';
